
/* eslint-disable complexity */
import {Permissions} from 'hooks/permissions'

import {RootState} from 'store'

import {
    Route,
} from 'navigation/types'

const organizationSettingsRoutes = ['Realm']
const queueSettingsRoutes = ['QueueSettings']
const channelSettingsRoutes = ['ChannelSettings']
const statisticsRoutes = ['QueueStatistics']
const transcriptRoutes = ['Transcript']

export class PermissionGuard {
    permissions: Permissions | null = null

    route: Route | undefined

    state: RootState | undefined

    realmId = ''

    channelId = ''

    // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-empty-function
    public replaceRoute(_newRoute: Route) { }

    public setPermissions(permissions: Permissions) {
        this.permissions = permissions
    }

    public setRoute(route: Route, replaceRoute: (newRoute: Route) => void) {
        this.route = route
        this.replaceRoute = replaceRoute
    }

    public setState(state: RootState) {
        this.state = state
    }

    public setRealmId(id: string) {
        this.realmId = id
    }

    public setChannelId(id: string) {
        this.channelId = id
    }

    public isAllowedRoute() {
        if (this.hasNewPermissionsEnabled() === false) {
            return true
        }

        if (!this.route || !this.permissions) {
            return false
        }

        if (organizationSettingsRoutes.indexOf(this.route.name) !== -1) {
            // eslint-disable-next-line no-extra-parens
            if ((this.route as Route<'Realm'>).params?.tab === 'queues' && !this.hasViewRealmQueuePermissions()) {
                this.replaceRoute({name: 'PermissionDenied'})
                return false
                // eslint-disable-next-line no-extra-parens
            } else if ((this.route as Route<'Realm'>).params?.tab === 'sites' && !this.hasViewRealmSitePermissions()) {
                this.replaceRoute({name: 'PermissionDenied'})
                return false
                // eslint-disable-next-line no-extra-parens
            } else if ((this.route as Route<'Realm'>).params?.tab === 'permissions' && !this.hasNewPermissionsEnabled()) {
                this.replaceRoute({name: 'PermissionDenied'})
                return false
                // eslint-disable-next-line no-extra-parens
            } else if ((this.route as Route<'Realm'>).params?.tab &&
            // eslint-disable-next-line no-extra-parens
                ((this.route as Route<'Realm'>).params?.tab !== 'queues' && (this.route as Route<'Realm'>).params?.tab !== 'sites') && !this.hasViewRealmPermissions()) {
                this.replaceRoute({name: 'PermissionDenied'})
                return false
            }
        }

        if (channelSettingsRoutes.indexOf(this.route.name) !== -1) {
            // eslint-disable-next-line no-extra-parens
            const channelId = (this.route as Route<'ChannelSettings'>).params?.channel_id
            if (!this.hasManageRealmChannelPermissions(channelId)) {
                this.replaceRoute({name: 'PermissionDenied'})
                return false
            }
        }

        if (queueSettingsRoutes.indexOf(this.route.name) !== -1) {
            if (!this.hasViewRealmQueuePermissions()) {
                this.replaceRoute({name: 'PermissionDenied'})
                return false
            }
        }

        if (transcriptRoutes.indexOf(this.route.name) !== -1) {
            if (!this.hasViewRealmTranscriptPermissions() && !this.hasManageRealmChannelPermissions(this.channelId)) {
                this.replaceRoute({name: 'PermissionDenied'})
                return false
            }
        }

        if (statisticsRoutes.indexOf(this.route.name) !== -1) {
            if (!this.hasViewRealmStatisticsPermissions()) {
                this.replaceRoute({name: 'PermissionDenied'})
                return false
            }

            // eslint-disable-next-line no-extra-parens
            if ((this.route as Route<'QueueStatistics'>).params?.tab === 'transcript' && !this.hasViewRealmTranscriptPermissions()) {
                this.replaceRoute({name: 'PermissionDenied'})
                return false
            }

            // eslint-disable-next-line no-extra-parens
            if ((this.route as Route<'QueueStatistics'>).params?.tab === 'metadata' && !this.hasViewRealmMetadataPermissions()) {
                this.replaceRoute({name: 'PermissionDenied'})
                return false
            }
        }

        return true
    }

    // site
    public hasViewRealmSitePermissions(): boolean {
        return !!this.permissions?.view_realm_site
    }

    public hasManageRealmSitePermissions(): boolean {
        return !!this.permissions?.manage_realm_site
    }

    // queue
    public hasViewRealmQueuePermissions(): boolean {
        return !!this.permissions?.view_realm_queue
    }

    public hasManageRealmQueuePermissions(): boolean {
        return !!this.permissions?.manage_realm_queue
    }

    public hasUpdateQueueClosedPermissions(): boolean {
        return !!this.permissions?.update_queue_closed
    }

    // statistics
    public hasViewRealmStatisticsPermissions(): boolean {
        return !!this.permissions?.view_realm_statistics
    }

    public hasViewRealmStatisticsAgentPermissions(): boolean {
        return !!this.permissions?.view_realm_statistics_agent
    }

    // activity
    public hasViewRealmActivityPermissions(): boolean {
        return !!this.permissions?.view_realm_activity
    }

    // transcripts
    public hasViewRealmTranscriptPermissions(): boolean {
        return !!this.permissions?.view_realm_transcript
    }

    public hasDeleteRealmTranscriptPermissions(): boolean {
        return !!this.permissions?.delete_realm_transcript
    }

    // metadata
    public hasViewRealmMetadataPermissions(): boolean {
        return !!this.permissions?.view_realm_metadata
    }

    // realm
    public hasViewRealmPermissions(): boolean {
        return !!this.permissions?.view_realm
    }

    public hasManageRealmPermissions(): boolean {
        if (!this.route || !this.state) {
            return false
        }

        // eslint-disable-next-line no-extra-parens
        const {
            user_realms, user_id,
        } = this.state?.ninchat || {}

        const isOwner = user_realms?.[this.realmId]?.realm_attrs?.owner_id === user_id
        const hasPermissions = !!this.permissions?.manage_realm

        return isOwner || hasPermissions
    }

    // channel
    public hasManageRealmChannelPermissions(channelId: string): boolean {
        if (!this.state) {
            return false
        }

        const {ninchat} = this.state
        const userId = this.state?.ninchat.user_id

        if (userId) {
            const isChannelOwner = ninchat.user_channels[channelId]?.channel_attrs?.owner_id === userId
            const isChannelOperator = ninchat?.user_channels?.[channelId]?.channel_members?.[userId]?.member_attrs?.operator || false

            return isChannelOwner || isChannelOperator
        }

        return false
    }

    // new or old permissions
    // eslint-disable-next-line class-methods-use-this
    public hasNewPermissionsEnabled(): boolean {
        return !!this.state?.ninchat.user_realms?.[this.realmId]?.realm_attrs?.permissions_enabled
    }
}

export default new PermissionGuard()
